// The shared time ruler for a stack of tracks.
//
// A stack shows one timeline, so it gets one x-axis. Repeating the date strip
// under every track costs vertical space (the scarce dimension once the map, the
// transport and several tracks are on screen) and reads as several charts that
// happen to be adjacent rather than as one instrument.
//
// The ruler MUST be created with the same gutterLeft as the tracks it heads, or
// its ticks will not sit above the data they label.

import { getCursor, indexAtTime, setCursor, subscribeCursor, timeAtIndex, timesOf } from "../time/cursor.js";
import { getWindowHours, subscribeWindow } from "../time/window.js";
import { fmtDayHM, fmtTickLabel, subscribeTimeMode } from "../ui/time.js";

export interface TimeRulerConfig {
  xLabels: string[];
  /** Must match the tracks' ChartConfig.gutterLeft. */
  gutterLeft: number;
  /** Right margin; must match the chart's (10 px). */
  gutterRight?: number;
  /** Label for a major tick, or null to skip. Defaults to midnight -> "Mon 21". */
  majorTick?: (label: string, index: number) => string | null;
}

export interface TimeRuler {
  update(xLabels?: string[]): void;
  destroy(): void;
}

// Major ticks come from the shared clock module (ui/time.ts): site-local
// midnights by default, UTC midnights when the console's clock is toggled.
function defaultMajor(label: string): string | null {
  return fmtTickLabel(label);
}

function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v.length > 0 ? v : fallback;
}

export function createTimeRuler(canvas: HTMLCanvasElement, config: TimeRulerConfig): TimeRuler {
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("ruler: 2d context unavailable");
  const g = ctx;
  let labels = config.xLabels;
  const gutterRight = config.gutterRight ?? 10;
  let raf = false;

  function draw(): void {
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, rect.width, rect.height);

    const left = config.gutterLeft;
    const right = rect.width - gutterRight;
    const n = labels.length;
    if (right <= left || n === 0) return;
    const denom = n > 1 ? n - 1 : 1;
    const xPx = (i: number): number => left + ((right - left) * i) / denom;

    const muted = cssVar("--muted", "#78848f");
    // --line-2, NOT --rule. There has never been a `--rule` token: this read
    // fell through to its hardcoded fallback in every theme, so the axis line
    // and the tick marks were a fixed light grey on Day, Dusk and Night alike —
    // correct-looking on white and wrong on both dark themes, where every other
    // hairline lightens and this one did not.
    //
    // Fixed by pointing at the token that already means this rather than by
    // defining `--rule` to match it. A second name for one value is how the
    // stale tables and the 14-day label happened: two things that must agree,
    // kept in agreement by hand. `--line-2` is the stronger of the two hairline
    // tokens and is themed for all three modes.
    //
    // Shared file: this is the ruler under BOTH consoles, so it fixes both.
    const rule = cssVar("--line-2", "#d7dee5");
    const accent = cssVar("--crit", "#b23b3b");
    const baseY = rect.height - 1;

    g.font = "11px system-ui, -apple-system, sans-serif";
    g.strokeStyle = rule;
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(left, baseY + 0.5);
    g.lineTo(right, baseY + 0.5);
    g.stroke();

    const major = config.majorTick ?? defaultMajor;
    g.textBaseline = "alphabetic";
    // Day labels are drawn only where they FIT. Every midnight used to get one
    // regardless of how much room it had, which is fine on a wide console and
    // illegible anywhere else: on a phone the ruler is about 190px across seven
    // days, so the midnights land ~21px apart and a "Sat 15" is twice that. The
    // labels overprinted into a grey smear.
    //
    // Measured, not stepped. A rule like "every other day under N pixels" needs
    // a threshold that is wrong for some font, zoom or window width; asking the
    // canvas how wide the text actually is cannot be wrong. The DAY TICK is
    // still drawn at full height when its label is dropped — the boundary is
    // real and worth marking even when it cannot be named.
    const MIN_LABEL_GAP = 6;
    let lastLabelRight = -Infinity;
    for (let i = 0; i < n; i++) {
      const lbl = major(labels[i], i);
      const isDay = lbl !== null;
      const isSix = labels[i].endsWith(":00") && i % 6 === 0;
      if (!isDay && !isSix) continue;
      const x = Math.round(xPx(i)) + 0.5;
      g.strokeStyle = rule;
      g.beginPath();
      g.moveTo(x, baseY - (isDay ? 9 : 4));
      g.lineTo(x, baseY);
      g.stroke();
      if (!isDay || lbl === null) continue;

      // First label hugs the left edge so it is not clipped by the gutter.
      const w = g.measureText(lbl).width;
      const tx = i === 0 ? left + 1 : xPx(i);
      const tLeft = i === 0 ? tx : tx - w / 2;
      if (tLeft < lastLabelRight + MIN_LABEL_GAP) continue; // would collide
      if (tLeft + w > right) continue; // would spill past the plot's right edge
      g.fillStyle = muted;
      g.textAlign = i === 0 ? "left" : "center";
      g.fillText(lbl, tx, baseY - 12);
      lastLabelRight = tLeft + w;
    }

    // The work window, drawn as a region the way a DAW draws its loop: the
    // briefing's "12 h" is a span with a start you placed, so the timeline should
    // show it rather than leaving a number in a panel nothing corroborates.
    // Painted before the caret so the playhead reads as the window's leading edge.
    const cur = getCursor();
    if (cur !== null) {
      const startIdx = indexAtTime(labels, cur);
      if (startIdx !== null) {
        const times = timesOf(labels);
        const endT = cur + getWindowHours() * 3_600_000;
        const lastT = times[times.length - 1];
        // Clamp to the end of the forecast rather than running off the axis: a
        // window that extends past the data is a real thing to see.
        const endIdx =
          Number.isFinite(lastT) && endT >= lastT ? n - 1 : (indexAtTime(labels, endT) ?? n - 1);
        const x0 = xPx(startIdx);
        const x1 = xPx(endIdx);
        if (x1 > x0) {
          const acc = cssVar("--accent", "#0f6b74");
          g.fillStyle = acc;
          g.globalAlpha = 0.3;
          g.fillRect(x0, baseY - 14, x1 - x0, 14);
          g.globalAlpha = 1;
          // Solid rules at both ends: the tint alone reads as a smudge on a
          // projector, and the closing edge is the number people ask about.
          g.strokeStyle = acc;
          g.lineWidth = 2;
          g.beginPath();
          g.moveTo(Math.round(x1) - 0.5, baseY - 14);
          g.lineTo(Math.round(x1) - 0.5, baseY);
          g.moveTo(Math.round(x0) + 0.5, baseY - 14);
          g.lineTo(Math.round(x0) + 0.5, baseY);
          g.stroke();
        }
      }
    }

    // The playhead's own marker: a caret plus the instant it sits on, so the
    // stack has one authoritative time readout instead of one per tooltip.
    const idx = indexAtTime(labels, getCursor());
    if (idx === null) return;
    const x = xPx(idx);
    g.fillStyle = accent;
    g.beginPath();
    g.moveTo(x, baseY - 5);
    g.lineTo(x - 4.5, baseY - 12);
    g.lineTo(x + 4.5, baseY - 12);
    g.closePath();
    g.fill();

    const t = timesOf(labels)[idx];
    if (!Number.isFinite(t)) return;
    // Through the shared clock: site-local by default (the chip must name the
    // hour of the tick it sits over, not that hour in the browser's zone), and
    // explicitly-labelled UTC when the console's clock is toggled.
    const txt = fmtDayHM(t);
    g.font = "600 10px ui-monospace, Menlo, monospace";
    const tw = g.measureText(txt).width;
    // Clamped so the chip stays inside the plot at either end.
    const bx = Math.max(left, Math.min(right - tw - 8, x - tw / 2 - 4));
    g.fillStyle = accent;
    g.fillRect(bx, baseY - 36, tw + 8, 14);
    g.fillStyle = cssVar("--surface", "#ffffff");
    g.textAlign = "left";
    g.fillText(txt, bx + 4, baseY - 26);
  }

  function requestDraw(): void {
    if (raf) return;
    raf = true;
    requestAnimationFrame(() => {
      raf = false;
      draw();
    });
  }

  // --- Scrubbing ------------------------------------------------------------
  //
  // The ruler is the ONE control that moves the shared playhead. It used to be
  // draw-only, and the playhead followed whatever chart the pointer happened to
  // cross — which meant the console had no way to be told "go to this hour and
  // stay there", and every stray mouse movement over the stack repainted every
  // surface. Reading a track is now inspection; moving the console is here.
  //
  // Click seeks. Press and drag scrubs. Pointer capture means the drag survives
  // leaving the strip, which is a 30px-tall target — without it, a scrub that
  // strays a few pixels high would simply stop.

  /** Map a client x to a label index, using the same geometry draw() uses. */
  function indexAtClientX(clientX: number): number | null {
    const rect = canvas.getBoundingClientRect();
    const left = config.gutterLeft;
    const right = rect.width - gutterRight;
    const n = labels.length;
    if (right <= left || n === 0) return null;
    const denom = n > 1 ? n - 1 : 1;
    const frac = (clientX - rect.left - left) / (right - left);
    const i = Math.round(frac * denom);
    return i < 0 ? 0 : i > n - 1 ? n - 1 : i;
  }

  function seekTo(clientX: number): void {
    const i = indexAtClientX(clientX);
    if (i === null) return;
    setCursor(timeAtIndex(labels, i));
  }

  let scrubbing = false;

  function onDown(e: PointerEvent): void {
    scrubbing = true;
    canvas.setPointerCapture(e.pointerId);
    seekTo(e.clientX);
    e.preventDefault(); // no text selection while dragging across the strip
  }
  function onMove(e: PointerEvent): void {
    if (!scrubbing) return;
    seekTo(e.clientX);
  }
  function onUp(e: PointerEvent): void {
    if (!scrubbing) return;
    scrubbing = false;
    if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
  }

  canvas.style.cursor = "ew-resize";
  canvas.style.touchAction = "none"; // let a finger scrub instead of scrolling the page
  canvas.addEventListener("pointerdown", onDown);
  canvas.addEventListener("pointermove", onMove);
  canvas.addEventListener("pointerup", onUp);
  canvas.addEventListener("pointercancel", onUp);

  const unsubscribe = subscribeCursor(requestDraw);
  const unsubWindow = subscribeWindow(requestDraw);
  const unsubClock = subscribeTimeMode(requestDraw);
  const ro = new ResizeObserver(requestDraw);
  ro.observe(canvas.parentElement ?? canvas);
  draw();

  return {
    update(next?: string[]): void {
      if (next !== undefined) labels = next;
      draw();
    },
    destroy(): void {
      unsubscribe();
      unsubWindow();
      unsubClock();
      ro.disconnect();
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointercancel", onUp);
    },
  };
}
